import { Action, Reducer } from 'redux';
import {
  ACTION, ClearBowtieData, RequestBowtie, ReceiveBowtie, FailBowtie,
  RequestDraft, ReceiveDraft, FailDraft,
  RequestConnectDraft, ReceiveConnectDraft, FailConnectDraft,
  RequestCase, ReceiveCase, FailCase,
  RequestConnectedCases, ReceiveConnectedCases, FailConnectedCases,
  CleanData,
  RequestBowtieFavourite,
  ReceiveBowtieFavourite,
  IncrementRequestId
} from './action-types';
import { BowtieInstanceState, BowtieStoreState } from './types-store';
import { produce } from 'immer';

export const INITIAL_BOWTIE_INSTANCE_STATE: BowtieInstanceState = {
  currentCaseNumber: undefined,
  currentDraftId: undefined,
  fetchingBowtie: false,
  caseDataModel: undefined,
  bowtieLayout: undefined,
  caseTemplate: undefined,
  fetchingDraft: false,
  draftRequestId: 0,
  draftData: undefined,
  fetchingConnections: [],
  casesRequiringUpdate: [],
  connectionTypes: undefined,
  inspectionEvaluations: {
    fetching: [],
    evaluationStatuses: {}
  },
  connectedCases: {
    fetching: {},
    connected: {}
  },
  creatingBowtieFavourite: false,
  bowtieFavouriteId: undefined,
  requestId: 0
};

export const INITIAL_BOWTIE_STATE: BowtieStoreState = {
  instances: {},
  texts: {
    id: 0,
    texts: []
  },
  textsLanguage: null,
  textsFetching: false
};

const reducers: { [indexer: string]: Reducer<BowtieStoreState, Action> } = {

  [ACTION.CLEAR_BOWTIE_DATA]: (state: BowtieStoreState, action: ClearBowtieData): BowtieStoreState => {
    return produce(state, draft => {
      draft.instances[action.instanceKey] = {
          ...INITIAL_BOWTIE_INSTANCE_STATE,
          bowtieFavouriteId: state.instances[action.instanceKey]?.bowtieFavouriteId,
          requestId: state.instances[action.instanceKey]?.requestId || 0
      };
      return draft;
    });
  },

  [ACTION.REQUEST_BOWTIE]: (state: BowtieStoreState, action: RequestBowtie): BowtieStoreState => {
    return produce(state, draft => {
      draft.instances[action.instanceKey] = {
        ...draft.instances[action.instanceKey],
        currentCaseNumber: action.caseNumber,
        currentDraftId: action.draftId,
        fetchingBowtie: true
      };
      return draft;
    });
  },

  [ACTION.RECEIVE_BOWTIE]: (state: BowtieStoreState, action: ReceiveBowtie): BowtieStoreState => {
    const currentInstanceState = state.instances?.[action.instanceKey];
    if (currentInstanceState.currentCaseNumber !== action.caseNumber ||
      currentInstanceState.currentDraftId !== action.draftId) {
      return state;
    }
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.fetchingBowtie = false;
        instance.caseDataModel = action.caseDataModel;
        instance.bowtieLayout = action.bowtieLayout;
        instance.caseTemplate = action.caseTemplate;
        instance.inspectionEvaluations = {
          ...instance.inspectionEvaluations,
          evaluationStatuses: {
            ...instance.inspectionEvaluations?.evaluationStatuses,
            ...action.evaluations
          }
        };
      }
    });
  },

  [ACTION.FAIL_BOWTIE]: (state: BowtieStoreState, action: FailBowtie): BowtieStoreState => {
    const currentInstanceState = state.instances?.[action.instanceKey];
    if (currentInstanceState.currentCaseNumber !== action.caseNumber ||
      currentInstanceState.currentDraftId !== action.draftId) {
      return state;
    }
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.fetchingBowtie = false;
        instance.caseDataModel = undefined;
        instance.caseTemplate = undefined;
      }
    });
  },

  [ACTION.REQUEST_DRAFT]: (state: BowtieStoreState, action: RequestDraft): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.fetchingDraft = true;
        instance.draftRequestId = action.requestId;
      }
    });
  },

  [ACTION.RECEIVE_DRAFT]: (state: BowtieStoreState, action: ReceiveDraft): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance?.draftRequestId === action.requestId) {
        instance.fetchingDraft = false;
        instance.draftData = action.draftData;
        instance.connectionTypes = action.connectionTypes;
        instance.casesRequiringUpdate = instance.casesRequiringUpdate
          .filter(caseNumber => instance.fetchingConnections.includes(caseNumber));
      }
    });
  },

  [ACTION.FAIL_DRAFT]: (state: BowtieStoreState, action: FailDraft): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance?.draftRequestId === action.requestId) {
        instance.fetchingDraft = false;
        instance.casesRequiringUpdate = instance.casesRequiringUpdate
          .filter(caseNumber => instance.fetchingConnections.includes(caseNumber));
      }
    });
  },

  [ACTION.REQUEST_CASE]: (state: BowtieStoreState, action: RequestCase): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.inspectionEvaluations = {
          ...instance.inspectionEvaluations,
          fetching: [
            ...instance.inspectionEvaluations.fetching,
            action.caseNumber
          ]
        };
      }
    });
  },

  [ACTION.RECEIVE_CASE]: (state: BowtieStoreState, action: ReceiveCase): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.inspectionEvaluations = {
          ...instance.inspectionEvaluations,
          fetching: instance.inspectionEvaluations.fetching.filter(caseNumber => caseNumber !== action.caseNumber),
          evaluationStatuses: {
            ...instance.inspectionEvaluations.evaluationStatuses,
            [action.caseNumber]: action.evaluation
          }
        };
      }
    });
  },

  [ACTION.FAIL_CASE]: (state: BowtieStoreState, action: FailCase): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.inspectionEvaluations = {
          ...instance.inspectionEvaluations,
          fetching: instance.inspectionEvaluations.fetching.filter(caseNumber => caseNumber !== action.caseNumber)
        };
      }
    });
  },

  [ACTION.REQUEST_CONNECTED_CASES]: (state: BowtieStoreState, action: RequestConnectedCases): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.connectedCases = {
          ...instance.connectedCases,
          fetching: {
            [action.caseNumber]: [
              ...(instance.connectedCases.fetching?.[action.caseNumber] ?? []),
              action.connectionType
            ]
          }
        };
      }
    });
  },

  [ACTION.RECEIVE_CONNECTED_CASES]: (state: BowtieStoreState, action: ReceiveConnectedCases): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.connectedCases = {
          ...instance.connectedCases,
          fetching: {
            [action.caseNumber]: (instance.connectedCases.fetching?.[action.caseNumber] ?? [])
              .filter(connectionType => connectionType !== action.connectionType)
          },
          connected: {
            ...instance.connectedCases.connected,
            [action.caseNumber]: {
              ...(instance.connectedCases.connected?.[action.caseNumber] ?? {}),
              [action.connectionType]: action.connected
            }
          }
        };
      }
    });
  },

  [ACTION.FAIL_CONNECTED_CASES]: (state: BowtieStoreState, action: FailConnectedCases): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.connectedCases = {
          ...instance.connectedCases,
          fetching: {
            [action.caseNumber]: (instance.connectedCases.fetching?.[action.caseNumber] ?? [])
              .filter(connectionType => connectionType !== action.connectionType)
          }
        };
      }
    });
  },

  [ACTION.CLEAN_DATA]: (state: BowtieStoreState, action: CleanData): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        Object.assign(instance, {
          ...INITIAL_BOWTIE_INSTANCE_STATE,
          requestId: instance.requestId || 0,
          bowtieLayout: instance.bowtieLayout,
          draftRequestId: instance.draftRequestId || 0
        });
      }
    });
  },

  [ACTION.REQUEST_CONNECT_DRAFT]: (state: BowtieStoreState, action: RequestConnectDraft): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        if (!instance.fetchingConnections.some(c => c === action.caseNumber)) {
          instance.fetchingConnections.push(action.caseNumber);
          instance.casesRequiringUpdate.push(action.caseNumber);
        }
      }
    });
  },

  [ACTION.RECEIVE_CONNECT_DRAFT]: (state: BowtieStoreState, action: ReceiveConnectDraft): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.fetchingConnections = instance.fetchingConnections.filter(c => c !== action.caseNumber);
      }
    });
  },

  [ACTION.FAIL_CONNECT_DRAFT]: (state: BowtieStoreState, action: FailConnectDraft): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.fetchingConnections = instance.fetchingConnections.filter(c => c !== action.caseNumber);
      }
    });
  },

  [ACTION.REQUEST_BOWTIE_FAVOURITE]: (state: BowtieStoreState, action: RequestBowtieFavourite): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.creatingBowtieFavourite = true;
        instance.bowtieFavouriteId = undefined;
      }
    });
  },

  [ACTION.RECEIVE_BOWTIE_FAVOURITE]: (state: BowtieStoreState, action: ReceiveBowtieFavourite): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.creatingBowtieFavourite = false;
        instance.bowtieFavouriteId = action.bowtieFavouriteId;
      }
    });
  },

  [ACTION.INCREMENT_REQUEST_ID]: (state: BowtieStoreState, action: IncrementRequestId): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.requestId = (instance?.requestId ?? 0) + 1;
      }
    });
  }
};

export const reducer: Reducer<BowtieStoreState> = (state: BowtieStoreState = INITIAL_BOWTIE_STATE, action: any) => {
  const r = reducers[action.type];
  return (r && r(state, action)) || state;
};
